// GauntletHistory.tsx, recent gauntlet runs for the current mercenary
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import styles from './GauntletHistory.module.css';

interface HistoryParticipant {
  id: number;
  mercenaryId: number;
  hasDroppedOut: boolean;
  droppedOutAfterWave: number | null;
  rewardSilver: number;
  rewardXp: number;
  rewardItems: number;
  mercenary: { id: number; name: string; level: number; race: string };
}

interface HistoryWaveReport {
  wave: number;
  battleReportId: number;
}

interface HistoryRun {
  id: number;
  bracket: string;
  status: string;
  currentWave: number;
  accumulatedSilver: number;
  accumulatedXp: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  participants: HistoryParticipant[];
  waveReports: HistoryWaveReport[];
}

const BRACKET_LABEL: Record<string, string> = {
  iron: 'Iron', blood: 'Blood', legend: 'Legend',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatStatus(status: string, currentWave: number): string {
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  if (status === 'waiting') return 'Never started';
  return status;
}

const GauntletHistory: React.FC = () => {
  const [runs, setRuns] = useState<HistoryRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { mercenary } = useMercenary();

  useEffect(() => {
    axios.get('/api/gauntlet/history')
      .then(res => { setRuns(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) return <div className={styles.loading}>Loading history…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Gauntlet History</h2>
        <button className={styles.backBtn} onClick={() => navigate('/gauntlet')}>← Back to Hub</button>
      </div>

      {runs.length === 0 ? (
        <div className={styles.empty}>No gauntlet runs found. Enter the Gauntlet to start building your legacy.</div>
      ) : (
        <div className={styles.runList}>
          {runs.map(run => {
            const isExpanded = expanded.has(run.id);
            const wavesCompleted = run.status === 'completed'
              ? 5
              : run.status === 'failed' || run.status === 'waiting'
                ? Math.max(0, run.currentWave - (run.status === 'failed' ? 1 : 0))
                : run.currentWave;
            const date = formatDate(run.completedAt || run.startedAt || run.createdAt);
            const myParticipant = mercenary
              ? run.participants.find(p => p.mercenaryId === mercenary.id)
              : null;
            const myWaves = myParticipant
              ? myParticipant.hasDroppedOut
                ? (myParticipant.droppedOutAfterWave ?? 0)
                : wavesCompleted
              : wavesCompleted;

            return (
              <div key={run.id} className={styles.runCard} onClick={() => toggle(run.id)}>
                <div className={styles.runCardTop}>
                  <div className={styles.runMeta}>
                    <span className={styles.bracketBadge} data-bracket={run.bracket}>
                      {BRACKET_LABEL[run.bracket] ?? run.bracket} Gauntlet
                    </span>
                    <span className={styles.statusBadge} data-status={run.status}>
                      {formatStatus(run.status, run.currentWave)}
                    </span>
                    <span className={styles.runDate}>{date}</span>
                  </div>
                  <button
                    className={styles.viewBtn}
                    onClick={e => { e.stopPropagation(); navigate(`/gauntlet/${run.id}/report`); }}
                  >
                    Wave Reports ↗
                  </button>
                </div>

                {/* Wave progress dots */}
                <div className={styles.waveProgress}>
                  {[1, 2, 3, 4, 5].map(w => {
                    const done = w <= wavesCompleted;
                    const isCurrent = w === run.currentWave && run.status === 'failed';
                    return (
                      <div
                        key={w}
                        className={`${styles.waveDot} ${done ? styles.done : ''} ${isCurrent ? styles.failed : ''}`}
                        title={w === 5 ? 'Boss wave' : `Wave ${w}`}
                      >
                        {w}
                      </div>
                    );
                  })}
                  <span className={styles.waveLabel}>
                    {run.status === 'completed' ? 'All 5 waves cleared!'
                      : wavesCompleted > 0 ? `${wavesCompleted} wave${wavesCompleted !== 1 ? 's' : ''} cleared`
                      : 'Did not fight'}
                  </span>
                </div>

                {/* Reward totals */}
                {(run.accumulatedSilver > 0 || run.accumulatedXp > 0) && (
                  <div className={styles.rewardRow}>
                    {run.accumulatedSilver > 0 && (
                      <div className={styles.rewardItem}>
                        <strong>{run.accumulatedSilver.toLocaleString()}</strong> silver (party total)
                      </div>
                    )}
                    {run.accumulatedXp > 0 && (
                      <div className={styles.rewardItem}>
                        <strong>{run.accumulatedXp.toLocaleString()}</strong> XP (party total)
                      </div>
                    )}
                  </div>
                )}

                {/* Party names preview */}
                {run.participants.length > 0 && (
                  <div className={styles.partyRow}>
                    <span className={styles.partyLabel}>Party:</span>
                    {run.participants.map(p => (
                      <Link
                        key={p.id}
                        to={`/mercenary/${p.mercenary.id}`}
                        className={`${styles.partyName} ${mercenary && p.mercenaryId === mercenary.id ? styles.me : ''}`}
                        onClick={e => e.stopPropagation()}
                      >
                        {p.mercenary.name}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Expanded detail */}
                {isExpanded && (
                  <div className={styles.detailPanel} onClick={e => e.stopPropagation()}>
                    {/* Wave battle report links */}
                    {run.waveReports.length > 0 && (
                      <div className={styles.waveReports}>
                        {[...run.waveReports].sort((a, b) => a.wave - b.wave).map(wr => (
                          <Link
                            key={wr.wave}
                            to={`/battle-result/${wr.battleReportId}`}
                            className={styles.waveReportLink}
                          >
                            ⚔ Wave {wr.wave}
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Participants with drop-out info */}
                    <table className={styles.participantTable}>
                      <thead>
                        <tr>
                          <th>Mercenary</th>
                          <th>Level</th>
                          <th>Race</th>
                          <th>Waves Survived</th>
                          <th>Status</th>
                          <th>Silver</th>
                          <th>XP</th>
                          <th>Items</th>
                        </tr>
                      </thead>
                      <tbody>
                        {run.participants.map(p => {
                          const pWaves = p.hasDroppedOut
                            ? (p.droppedOutAfterWave ?? '—')
                            : wavesCompleted;
                          return (
                            <tr key={p.id}>
                              <td>
                                <Link to={`/mercenary/${p.mercenary.id}`}>
                                  {p.mercenary.name}
                                </Link>
                                {mercenary && p.mercenaryId === mercenary.id && (
                                  <span className={styles.droppedBadge}>(you)</span>
                                )}
                              </td>
                              <td>Lv.{p.mercenary.level}</td>
                              <td>{p.mercenary.race}</td>
                              <td>{pWaves}</td>
                              <td>
                                {p.hasDroppedOut
                                  ? <span style={{ color: '#c0392b' }}>Dropped out</span>
                                  : <span style={{ color: '#4caf72' }}>Survived</span>
                                }
                              </td>
                              <td>{p.rewardSilver ?? 0}</td>
                              <td>{p.rewardXp ?? 0}</td>
                              <td>{p.rewardItems ?? 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GauntletHistory;
